import { useEffect } from 'react';
import { usePendingApprovalsForSession } from './pending-approval-card';

/**
 * FRONT-1311 / BUG-071: keep a chat's unanswered approvals in view.
 *
 * A tier-3 approval is neither a new message nor a stream delta, so it fired
 * none of `chat-messages.tsx`'s scroll triggers. Its card mounted at the end of
 * the thread and grew the scroller while everything stayed silent — a reader
 * still pinned to the floor was left above a card that was already in the DOM,
 * and went looking in the Inspector instead. That is the whole ticket.
 *
 * Lives in its own hook rather than inline in `ChatMessages`: that component is
 * already ~500 lines, and Aikido flagged this PR for adding branching to it.
 * The behaviour is self-contained, so it extracts cleanly — no reason to make a
 * long component longer.
 *
 * Returns the count so the caller can also offer the "Jump to latest" pill (see
 * `shouldShowJumpPill`) to a reader who has scrolled up and would otherwise have
 * no route back to the card.
 */
export function useApprovalFollow(args: {
  sessionId: string;
  /** False once the reader scrolls up — they are never yanked back down. */
  autoScroll: boolean;
  scrollToBottom: (opts?: ScrollIntoViewOptions) => void;
}): number {
  const pendingApprovalCount = usePendingApprovalsForSession(args.sessionId).length;
  const { autoScroll, scrollToBottom } = args;

  // Instant, not smooth. The card grows a frame after it mounts (its args block
  // renders, the icon font lands), and a smooth animation aimed at the
  // pre-mount floor finishes short — verified live, it left the card half-cut
  // behind the composer. An approval is also the one moment where nothing
  // continues until the user acts, so landing on it immediately is the right
  // feel rather than a scroll to watch.
  useEffect(() => {
    if (!autoScroll || pendingApprovalCount === 0) return;
    scrollToBottom({ behavior: 'auto', block: 'end' });
  }, [pendingApprovalCount, autoScroll, scrollToBottom]);

  return pendingApprovalCount;
}
